const moveTowardsFractional = (movingPoint: Point, targetPoint: Point, fraction: number) => {
  return {
    x: movingPoint.x + (targetPoint.x - movingPoint.x) * fraction,
    y: movingPoint.y + (targetPoint.y - movingPoint.y) * fraction,
  }
}

const moveTowardsLength = (movingPoint: Point, targetPoint: Point, amount: number) => {
  const width = targetPoint.x - movingPoint.x
  const height = targetPoint.y - movingPoint.y
  const distance = Math.sqrt(width * width + height * height)

  return moveTowardsFractional(movingPoint, targetPoint, Math.min(1, amount / distance))
}

// Adjusts the ending position of a command
const adjustCommand = (cmd: any[], newPoint: Point) => {
  if (cmd.length > 2) {
    cmd[cmd.length - 2] = newPoint.x
    cmd[cmd.length - 1] = newPoint.y
  }
}

// Gives an {x, y} object for a command's ending position
const pointForCommand = (cmd: any[]): Point => {
  return {
    x: parseFloat(cmd[cmd.length - 2]),
    y: parseFloat(cmd[cmd.length - 1]),
  }
}

const pathRounding = (path: string, radius: number) => {
  // Split apart the path, handing concatonated letters and numbers
  const pathParts = path.split(/[,\s]/).reduce((parts, part) => {
    const match = part.match("([a-zA-Z])(.+)")

    if (match) {
      parts.push(match[1])
      parts.push(match[2])
    } else {
      parts.push(part)
    }

    return parts
  }, [] as string[])

  // Group the commands with their arguments for easier handling
  const commands = pathParts.reduce((parts, part) => {
    if (!isNaN(part as unknown as number) && parts.length) {
      parts[parts.length - 1].push(part)
    } else {
      parts.push([part])
    }

    return parts
  }, [] as any[])

  // The resulting commands, also grouped
  const resultCommands = []

  if (commands.length <= 1) {
    return commands
  } else {
    const startPoint = pointForCommand(commands[0])

    // Handle the close path case with a "virtual" closing line
    let virtualCloseLine = null
    if (commands[commands.length - 1][0] === "Z" && commands[0].length > 2) {
      virtualCloseLine = ["L", startPoint.x, startPoint.y]
      commands[commands.length - 1] = virtualCloseLine
    }

    // We always use the first command (but it may be mutated)
    resultCommands.push(commands[0])

    for (let cmdIndex = 1; cmdIndex < commands.length; cmdIndex++) {
      const prevCmd = resultCommands[resultCommands.length - 1]
      const curCmd = commands[cmdIndex]

      // Handle closing case
      // tslint:disable-next-line: triple-equals
      const nextCmd = curCmd == virtualCloseLine ? commands[1] : commands[cmdIndex + 1]

      // Nasty logic to decide if this path is a candidite.
      if (nextCmd && prevCmd && prevCmd.length > 2 && curCmd[0] === "L" && nextCmd.length > 2 && nextCmd[0] === "L") {
        // Calc the points we're dealing with
        const prevPoint = pointForCommand(prevCmd)
        const curPoint = pointForCommand(curCmd)
        const nextPoint = pointForCommand(nextCmd)

        // The start and end of the cuve are just our point moved towards the previous and next points, respectivly
        const curveStart = moveTowardsLength(curPoint, prevPoint, radius)
        const curveEnd = moveTowardsLength(curPoint, nextPoint, radius)

        // Adjust the current command and add it
        adjustCommand(curCmd, curveStart)
        curCmd.origPoint = curPoint
        resultCommands.push(curCmd)

        // The curve control points are halfway between the start/end of the curve and
        // the original point
        const startControl = moveTowardsFractional(curveStart, curPoint, 0.5)
        const endControl = moveTowardsFractional(curPoint, curveEnd, 0.5)

        // Create the curve
        const curveCmd = ["C", startControl.x, startControl.y, endControl.x, endControl.y, curveEnd.x, curveEnd.y]

        resultCommands.push(curveCmd)
      } else {
        // Pass through commands that don't qualify
        resultCommands.push(curCmd)
      }
    }

    // Fix up the starting point and restore the close path if the path was orignally closed
    if (virtualCloseLine) {
      const newStartPoint = pointForCommand(resultCommands[resultCommands.length - 1])
      resultCommands.push(["Z"])
      adjustCommand(resultCommands[0], newStartPoint)
    }
  }

  return resultCommands
}

export default pathRounding
